import { useEffect, useState } from "react";
import { Check, ChevronsUpDown, Plus, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { NumericMask } from "./NumericMask";
import { MobileCommandSheet } from "./MobileCommandSheet";
import { CATEGORY_CODE_MAX_DEPTH, flattenCategoryTree, getCategoryCodeSegment } from "../lib/category-tree";
import { MONEY_SCALE, cn, formatCategoryName, formatCurrency, moneyToUnits, normalizeCategoryName, normalizeUppercase, stringToColor } from "../lib/utils";
import { useIsMobileCombobox } from "../hooks/use-mobile-combobox";

function makeDetailId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `detail-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function toUnits(value) {
  return moneyToUnits(value);
}

function normalizeCommandSearch(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleUpperCase("pt-BR");
}

function commandFilter(value, search, keywords = []) {
  const needle = normalizeCommandSearch(search);
  if (!needle) return 1;

  const haystack = normalizeCommandSearch([value, ...keywords].filter(Boolean).join(" "));
  return haystack.includes(needle) ? 1 : 0;
}

export function createEmptyDetailItem() {
  return {
    id: makeDetailId(),
    description: "",
    amount: "",
  };
}

function DetailDescriptionCombobox({ value, onChange, suggestions, categories = [], placeholder, onAddChild }) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const isMobileCombobox = useIsMobileCombobox();
  const categoryItems = flattenCategoryTree(categories, { includeCollapsedChildren: true });
  const hasCategories = categoryItems.length > 0;
  const categoryNames = new Set(categoryItems.map((item) => normalizeCategoryName(item.name)));
  const plainSuggestions = suggestions
    .map((suggestion) => normalizeUppercase(suggestion))
    .filter(Boolean)
    .filter((suggestion, index, source) => source.indexOf(suggestion) === index)
    .filter((suggestion) => !categoryNames.has(normalizeCategoryName(suggestion)));
  const displayValue = hasCategories && value ? formatCategoryName(value) : value;
  const searchDisplayValue = hasCategories && searchValue ? formatCategoryName(searchValue) : searchValue;
  const commandListClassName = isMobileCombobox
    ? "max-h-none overflow-visible scroll-py-1"
    : "max-h-[min(58dvh,460px)] touch-pan-y overscroll-contain scroll-py-1 overflow-y-auto";
  const itemClassName = "min-h-12 min-w-0 gap-2 px-3 py-2.5 text-base sm:min-h-0 sm:px-2 sm:py-1.5 sm:text-sm";
  const normalizeValue = (nextValue) => hasCategories
    ? normalizeCategoryName(nextValue)
    : normalizeUppercase(nextValue);
  const getCategoryKeywords = (item) => [
    item.codigo,
    getCategoryCodeSegment(item),
    ...(item._pathNames || []),
    formatCategoryName(item.name),
  ].filter(Boolean);

  useEffect(() => {
    if (open) {
      setSearchValue("");
    }
  }, [open]);

  const triggerButton = (
    <Button
      type="button"
      variant="outline"
      role="combobox"
      aria-expanded={open}
      className={cn(
        "min-w-0 w-full justify-between bg-background font-normal text-foreground hover:bg-muted/30 hover:text-foreground",
        open && "border-primary/70 bg-background text-foreground ring-1 ring-primary/20 hover:bg-background hover:text-foreground"
      )}
      onClick={() => {
        if (isMobileCombobox) setOpen(true);
      }}
    >
      <span className={cn(
        "truncate",
        !value && "text-muted-foreground"
      )}>
        {displayValue || placeholder}
      </span>
      <ChevronsUpDown className={cn("ml-2 h-4 w-4 shrink-0", open ? "opacity-90" : "opacity-50")} />
    </Button>
  );

  const handleSearchChange = (nextValue) => {
    const normalizedValue = normalizeValue(nextValue);
    setSearchValue(normalizedValue);
    onChange(normalizedValue);
  };
  const input = (
    <CommandInput
      placeholder={hasCategories ? "Buscar ou inserir categoria..." : "Buscar ou inserir descrição..."}
      value={searchDisplayValue}
      onValueChange={handleSearchChange}
      className="h-12 text-base sm:h-11 sm:text-sm"
      autoComplete="off"
      autoFocus={isMobileCombobox}
    />
  );
  const commandList = (
      <CommandList
        className={commandListClassName}
        onWheel={(event) => event.stopPropagation()}
        onTouchMove={(event) => event.stopPropagation()}
        style={{
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          touchAction: "pan-y",
        }}
      >
        <CommandEmpty>
          {value ? (
            <button
              type="button"
              className="flex min-h-12 w-full items-center gap-2 px-4 py-2 text-left text-base hover:bg-accent sm:min-h-0 sm:text-sm"
              onClick={() => setOpen(false)}
            >
              <Plus className="h-4 w-4" />
              Usar "{searchDisplayValue || displayValue}"
            </button>
          ) : (
            hasCategories ? "Digite para inserir uma categoria" : "Digite para inserir uma descrição"
          )}
        </CommandEmpty>
        <CommandGroup>
          {categoryItems.map((item) => (
            <CommandItem
              key={item.id || item.name}
              value={formatCategoryName(item.name)}
              keywords={getCategoryKeywords(item)}
              className={itemClassName}
              style={{ paddingLeft: `${12 + (item._depth || 0) * 18}px` }}
              onSelect={(currentValue) => {
                onChange(normalizeValue(currentValue));
                setOpen(false);
              }}
            >
              <div
                className={cn("rounded-full shrink-0", item._depth > 0 ? "h-2 w-2 opacity-70" : "h-3 w-3")}
                style={{ backgroundColor: item.color || stringToColor(formatCategoryName(item.name)) }}
              />
              <span className="shrink-0 rounded border bg-muted px-1.5 py-0.5 font-mono text-[11px] font-semibold text-muted-foreground">
                {getCategoryCodeSegment(item)}
              </span>
              <span className="min-w-0 flex-1">
                <span className={cn("block truncate", item._depth > 0 && "text-sm text-muted-foreground")}>
                  {formatCategoryName(item.name)}
                </span>
                {isMobileCombobox && item._depth > 0 && item._pathNames?.length && (
                  <span className="block truncate text-xs text-muted-foreground/80">
                    {item._pathNames.map(formatCategoryName).join(" / ")}
                  </span>
                )}
              </span>
              <Check
                className={cn("h-4 w-4 shrink-0", normalizeValue(value) === normalizeValue(item.name) ? "opacity-100" : "opacity-0")}
              />
              {onAddChild && (item._depth || 0) < CATEGORY_CODE_MAX_DEPTH - 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="ml-1 h-8 w-8 shrink-0 text-muted-foreground hover:text-primary"
                  aria-label={`Adicionar filho de ${formatCategoryName(item.name)}`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setOpen(false);
                    onAddChild(item);
                  }}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              )}
            </CommandItem>
          ))}
          {plainSuggestions.map((suggestion) => (
            <CommandItem
              key={suggestion}
              value={suggestion}
              className={itemClassName}
              onSelect={(currentValue) => {
                onChange(normalizeValue(currentValue));
                setOpen(false);
              }}
            >
              <span className="min-w-0 flex-1 truncate">{suggestion}</span>
              <Check className={cn("h-4 w-4 shrink-0", normalizeValue(value) === normalizeValue(suggestion) ? "opacity-100" : "opacity-0")} />
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
  );
  const desktopCommand = (
    <Command className="max-h-[min(70dvh,560px)]" filter={commandFilter}>
      <CommandInput
        placeholder={hasCategories ? "Buscar ou inserir categoria..." : "Buscar ou inserir descrição..."}
        value={searchDisplayValue}
        onValueChange={handleSearchChange}
        className="h-12 text-base sm:h-11 sm:text-sm"
        autoComplete="off"
      />
      {commandList}
    </Command>
  );

  return (
    isMobileCombobox ? (
      <>
        {triggerButton}
        <MobileCommandSheet
          open={open}
          onOpenChange={setOpen}
          title={hasCategories ? "Categoria" : "Descrição"}
          input={input}
          commandProps={{ filter: commandFilter }}
        >
          {commandList}
        </MobileCommandSheet>
      </>
    ) : (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {triggerButton}
        </PopoverTrigger>
        <PopoverContent
          className="z-[80] w-[--radix-popover-trigger-width] max-w-[calc(100vw-3rem)] max-h-[min(70dvh,560px)] overflow-hidden p-0"
          align="start"
        >
          {desktopCommand}
        </PopoverContent>
      </Popover>
    )
  );
}

export function DetailedItemsEditor({ items, onChange, targetAmount, descriptionSuggestions = [], categories = [], onAddChild }) {
  const totalUnits = toUnits(targetAmount);
  const detailsUnits = items.reduce((sum, item) => sum + toUnits(item.amount), 0);
  const remainingUnits = totalUnits - detailsUnits;
  const isBalanced = totalUnits > 0 && remainingUnits === 0;

  const updateItem = (id, field, value) => {
    onChange(items.map((item) => (
      item.id === id ? { ...item, [field]: field === "description" ? normalizeUppercase(value) : value } : item
    )));
  };

  const removeItem = (id) => {
    onChange(items.filter((item) => item.id !== id));
  };

  const addItem = () => {
    onChange([...items, createEmptyDetailItem()]);
  };

  return (
    <div className="space-y-3 rounded-lg border border-border/80 p-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label>Detalhes</Label>
          <p className="text-xs text-muted-foreground">Subitens desta despesa</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addItem}>
          <Plus className="mr-2 h-4 w-4" />
          Incluir
        </Button>
      </div>

      {items.length === 0 ? (
        <Button
          type="button"
          variant="outline"
          className="h-14 w-full border-dashed text-primary"
          onClick={addItem}
        >
          <Plus className="mr-2 h-4 w-4" />
          Incluir detalhes
        </Button>
      ) : (
        <div className="space-y-3">
          {items.map((item, index) => (
            <div key={item.id} className="grid min-w-0 gap-2 rounded-md border bg-muted/20 p-3 md:grid-cols-[minmax(0,1fr)_150px_40px]">
              <div className="min-w-0 space-y-1">
                <Label className="text-xs">{categories.length ? "Categoria" : "Descrição"}</Label>
                <DetailDescriptionCombobox
                  value={item.description}
                  onChange={(value) => updateItem(item.id, "description", value)}
                  suggestions={descriptionSuggestions}
                  categories={categories}
                  onAddChild={onAddChild ? (parent) => onAddChild(parent, item.id) : undefined}
                  placeholder={`Item ${index + 1}`}
                />
              </div>
              <div className="min-w-0 space-y-1">
                <Label className="text-xs">Valor</Label>
                <NumericMask
                  value={item.amount}
                  onChange={(value) => updateItem(item.id, "amount", value)}
                  placeholder="0,00"
                  className="min-w-0 text-base"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="justify-self-end self-end text-muted-foreground hover:text-destructive"
                onClick={() => removeItem(item.id)}
                aria-label="Remover detalhe"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-2 text-sm sm:grid-cols-3">
        <div className="rounded-md bg-muted/40 px-3 py-2">
          <span className="block text-xs text-muted-foreground">Valor alvo</span>
          <span className="font-mono font-medium">{formatCurrency(totalUnits / MONEY_SCALE)}</span>
        </div>
        <div className="rounded-md bg-muted/40 px-3 py-2">
          <span className="block text-xs text-muted-foreground">Detalhes</span>
          <span className="font-mono font-medium">{formatCurrency(detailsUnits / MONEY_SCALE)}</span>
        </div>
        <div className={cn(
          "rounded-md px-3 py-2",
          isBalanced ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"
        )}>
          <span className="block text-xs opacity-80">Diferença</span>
          <span className="font-mono font-medium">{formatCurrency(remainingUnits / MONEY_SCALE)}</span>
        </div>
      </div>
    </div>
  );
}
